/**
 * wall-art-format-featured.js
 *
 * Per-format FEATURED IMAGE swap for the product gallery's slot shown 2nd
 * (internal id `wall-art-slot-7`, placed right after the hero). Mirrors
 * wall-art-format-static.js: a plain <img>, square-cropped via CSS. When the
 * customer changes format in the wall-art picker, the slide's main <img> + its
 * thumbnail <img> are swapped to the picked format's "featured slot 3" image
 * (the same GCS webp the Wall Art Product Features section shows as its 3rd
 * card), or the slot + thumbnail are hidden (data-no-featured, CSS-hidden)
 * when that format has no slot-3 image.
 *
 * Data source: a sibling
 *   <script type="application/json" id="FeaturedSlotData-<section.id>">
 *     [ { "format": "art_print", "url": "...", "alt": "..." }, ... ]
 *   </script>
 * (built in sections/main-product.liquid from product.metafields.custom
 * .featured_images_json — empty array / missing tag = no featured images.)
 * The same URL is used for the main slide + the thumbnail.
 *
 * Trigger: `wallart:selection:changed` from assets/wall-art-picker.js with
 * `detail = { format, size, frameColor, variantId, price }`.
 */
(function () {
  'use strict';

  const SLIDE_SELECTOR       = '[data-format-featured-slide]';
  const THUMB_SELECTOR       = '[data-format-featured-thumbnail-slide]';
  const MAIN_IMG_SELECTOR    = '[data-format-featured-main]';
  const THUMB_IMG_SELECTOR   = '[data-format-featured-thumbnail]';
  const DATA_SCRIPT_SELECTOR = 'script[type="application/json"][id^="FeaturedSlotData-"]';

  const controllers = new Map();

  function readData(sectionRoot) {
    const tag = sectionRoot.querySelector ? sectionRoot.querySelector(DATA_SCRIPT_SELECTOR) : null;
    if (!tag) return [];
    try {
      const parsed = JSON.parse(tag.textContent || 'null');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function findEntry(data, format) {
    if (!Array.isArray(data) || !format) return null;
    for (const e of data) {
      if (e && e.format === format) return e;
    }
    return null;
  }

  function findSectionRootForGallery(gallery) {
    return (
      gallery.closest('product-info') ||
      gallery.closest('[id^="ProductInfo-"]') ||
      gallery.closest('[id^="MainProduct-"]') ||
      document
    );
  }

  function setHidden(slide, thumb, hidden) {
    if (slide) {
      if (hidden) slide.setAttribute('data-no-featured', '');
      else slide.removeAttribute('data-no-featured');
    }
    if (thumb) {
      if (hidden) thumb.setAttribute('data-no-featured', '');
      else thumb.removeAttribute('data-no-featured');
    }
  }

  /** If this slot is the active slide but the new format has no image, click the
   *  first non-featured thumbnail so the carousel jumps to a visible slide. */
  function jumpAway(thumbsContainer) {
    if (!thumbsContainer) return;
    const items = thumbsContainer.querySelectorAll('[data-target]');
    for (const item of items) {
      if (item.matches(THUMB_SELECTOR)) continue;
      const btn = item.querySelector('button');
      if (btn) { btn.click(); return; }
    }
  }

  function applySwap(sectionRoot, data, detail) {
    if (!detail) return;
    const gallery = sectionRoot.querySelector ? sectionRoot.querySelector('media-gallery') : null;
    if (!gallery) return;

    const slide = gallery.querySelector(SLIDE_SELECTOR);
    const thumbsContainer = gallery.querySelector('[id^="GalleryThumbnails-"]');
    const thumb = thumbsContainer ? thumbsContainer.querySelector(THUMB_SELECTOR) : null;
    const entry = findEntry(data, detail.format);
    const isActive = !!(slide && slide.classList.contains('is-active'));

    if (!entry || !entry.url) {
      setHidden(slide, thumb, true);
      if (isActive) jumpAway(thumbsContainer);
      return;
    }

    setHidden(slide, thumb, false);

    const mainImg = slide ? slide.querySelector(MAIN_IMG_SELECTOR) : null;
    const thumbImg = thumb ? thumb.querySelector(THUMB_IMG_SELECTOR) : null;

    if (mainImg) {
      mainImg.setAttribute('src', entry.url);
      if (entry.alt) mainImg.setAttribute('alt', entry.alt);
      mainImg.removeAttribute('srcset');
    }
    if (thumbImg) {
      thumbImg.setAttribute('src', entry.url);
      if (entry.alt) thumbImg.setAttribute('alt', entry.alt);
      thumbImg.removeAttribute('srcset');
    }
  }

  function initForGallery(gallery) {
    const sectionRoot = findSectionRootForGallery(gallery);
    const sectionId = String(gallery.id || '').replace(/^MediaGallery-/, '');
    if (!sectionId) return;

    if (controllers.has(sectionId)) {
      try { controllers.get(sectionId).abort(); } catch { /* ignore */ }
      controllers.delete(sectionId);
    }

    const data = readData(sectionRoot);
    const abortController = new AbortController();
    controllers.set(sectionId, abortController);

    const onChange = (event) => applySwap(sectionRoot, data, event && event.detail);

    sectionRoot.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });
    document.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });

    if (window.__wallArtCurrentSelection) {
      applySwap(sectionRoot, data, window.__wallArtCurrentSelection);
    }
  }

  function initAll(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    if (root && root.matches && root.matches('media-gallery')) initForGallery(root);
    scope.querySelectorAll('media-gallery').forEach((g) => initForGallery(g));
  }

  function destroySection(sectionId) {
    const ctrl = controllers.get(sectionId);
    if (!ctrl) return;
    try { ctrl.abort(); } catch { /* ignore */ }
    controllers.delete(sectionId);
  }

  function start() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = event && event.target instanceof Element ? event.target : document;
      initAll(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const detailSectionId = String((event && event.detail && event.detail.sectionId) || '').trim();
      if (detailSectionId) { destroySection(detailSectionId); return; }
      const target = event && event.target instanceof Element ? event.target : null;
      if (!target) return;
      const gallery = (target.matches && target.matches('media-gallery'))
        ? target
        : (target.querySelector && target.querySelector('media-gallery'));
      if (!gallery) return;
      const derived = String(gallery.id || '').replace(/^MediaGallery-/, '');
      if (derived) destroySection(derived);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
